import moment from 'moment';

/**
 * Date Formatting Utilities
 * Provides consistent date formatting across the application
 */

/**
 * Formats a date range from month/year values
 * @param {string|number} startMonth - Start month (1-12)
 * @param {string|number} startYear - Start year
 * @param {string|number} [endMonth] - End month (1-12), optional
 * @param {string|number} [endYear] - End year, optional
 * @returns {string} Formatted date range (e.g., "Jan 2020 - Dec 2022" or "Jan 2020 - Present")
 */
export function formatDateRange(startMonth, startYear, endMonth, endYear) {
  const startDateString = moment(`${startYear}-${startMonth}`, 'YYYY-MM').format('MMM YYYY');

  if (!endYear || endYear === null || endYear === '') {
    return `${startDateString} - Present`;
  }

  const endDateString = moment(`${endYear}-${endMonth}`, 'YYYY-MM').format('MMM YYYY');

  return `${startDateString} - ${endDateString}`;
}

/**
 * Formats a single month/year value
 * @param {string|number} month - Month (1-12)
 * @param {string|number} year - Year
 * @returns {string} Formatted date (e.g., "Jan 2020")
 */
export function formatMonthYear(month, year) {
  return moment(`${year}-${month}`, 'YYYY-MM').format('MMM YYYY');
}

/**
 * Formats a full date string
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "January 15, 2020")
 */
export function formatFullDate(dateString) {
  return moment(dateString).format('MMMM DD, YYYY');
}

/**
 * Formats a date for input fields (YYYY-MM-DD)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date for input
 */
export function formatDateForInput(date) {
  return moment(date).format('YYYY-MM-DD');
}

/**
 * Gets the current year
 * @returns {number} Current year
 */
export function getCurrentYear() {
  return moment().year();
}

/**
 * Calculates the difference in months between two dates
 * @param {string|number} startMonth - Start month (1-12)
 * @param {string|number} startYear - Start year
 * @param {string|number} endMonth - End month (1-12)
 * @param {string|number} endYear - End year
 * @returns {number} Number of months between dates
 */
export function getMonthsDifference(startMonth, startYear, endMonth, endYear) {
  const startDate = moment(`${startYear}-${startMonth}`, 'YYYY-MM');
  const endDate = moment(`${endYear}-${endMonth}`, 'YYYY-MM');
  return endDate.diff(startDate, 'months');
}
